import { useState } from 'react';
import type { StepProps } from '../OBWWizard';
import { portalOriginFor } from '../portal-origin';
import { SOW_CONTENT } from '../content/sow';

// ADR-073 (card-first) / ADR-076 — SOW completion bookend (staff fork). On
// confirm it provisions the subscriber via portal-api POST /portal/sow/provision
// (T0, HMAC-validated by the SOW launch token), which creates the User + Account
// (paid_pending_activation, selected tier) + Domain and emails the activation
// link. Account-less until this step. A §9 fork of BowCompleteStep; the deltas
// are the route, the sow token, and the plan/promo carried into provision.

type Phase = 'confirm' | 'submitting' | 'done' | 'error';

interface ProvisionResult {
  ok?: boolean;
  accountId?: string;
  customerNumber?: string;
  emailSent?: boolean;
  error?: string;
}

const TIER_LABEL: Record<string, string> = { T1: 'NexaGrowth', T2: 'NexaSuccess', T3: 'NexaContext' };

export function SowCompleteStep({ data, onJumpToStep, onNext }: StepProps) {
  const c = SOW_CONTENT.complete;

  const businessName = ((data?.businessName as string) ?? '').trim();
  const ownerEmail = ((data?.businessEmail as string) ?? '').trim();
  const domainUrl = ((data?.domainUrl as string) ?? '').trim();
  const nexaConsultant = ((data?.nexaConsultant as string) ?? '').trim();
  const selectedTier = ((data?.selectedTier as string) ?? '').trim();
  const promoCode = ((data?.promoCode as string) ?? '').trim();
  const sowToken = (data?.sowToken as string) ?? '';

  const [phase, setPhase] = useState<Phase>('confirm');
  const [error, setError] = useState('');
  const [recover, setRecover] = useState<'email' | 'domain' | 'plan' | null>(null);
  const [result, setResult] = useState<ProvisionResult | null>(null);

  const missing: string[] = [];
  if (!businessName) missing.push('business name');
  if (!ownerEmail) missing.push('subscriber email');
  if (!domainUrl) missing.push('domain');
  if (!selectedTier) missing.push('plan');

  async function provision() {
    setError('');
    setRecover(null);
    if (missing.length > 0) {
      setError(`Please go back and complete: ${missing.join(', ')}.`);
      return;
    }
    if (!sowToken) {
      setError('This onboarding session has no launch token. Please re-launch SOW from the dashboard.');
      return;
    }
    setPhase('submitting');
    try {
      const res = await fetch(`${portalOriginFor(data)}/portal/sow/provision`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: sowToken,
          businessName,
          ownerEmail,
          domainUrl,
          selectedTier,
          promoCode,
          nexaConsultant,
          jurisdiction: (data?.jurisdiction as string) ?? '',
          persona: {
            heroHeadline: data?.heroHeadline ?? '',
            audienceNoun: data?.audienceNoun ?? '',
            placeNoun: data?.placeNoun ?? '',
            funnel: data?.funnel ?? [],
          },
        }),
      });
      const body = (await res.json()) as ProvisionResult;
      if (!res.ok || body.error) {
        if (body.error === 'email_already_exists') {
          setError('A subscriber with that email already exists. Change the email below, or associate the existing user from Customers.');
          setRecover('email');
        } else if (body.error === 'domain_already_registered') {
          setError('That domain is already connected to a Nexanomics account. Change the domain to continue.');
          setRecover('domain');
        } else if (body.error === 'missing_plan') {
          setError('Please go back and choose a plan.');
          setRecover('plan');
        } else if (body.error === 'token_expired') {
          setError('This onboarding link has expired. Please re-launch SOW from the dashboard.');
        } else {
          setError('Could not create the account. Please try again, or check the subscriber details.');
        }
        setPhase('error');
        return;
      }
      setResult(body);
      setPhase('done');
    } catch {
      setError('Could not reach the server. Please check your connection and try again.');
      setPhase('error');
    }
  }

  if (phase === 'done') {
    return (
      <div className="obw-step-content">
        <div className="obw-ai-status" role="status" style={{ borderColor: 'var(--success)' }}>
          <span className="obw-ai-done" aria-hidden="true">✓</span>
          <span><strong style={{ color: 'var(--text)' }}>{c.successHeading}</strong></span>
        </div>
        <p className="obw-subtitle">{c.successBody}</p>
        <ul className="obw-steps-preview" aria-label="Created">
          <li><span className="obw-step-num" aria-hidden="true">✓</span><span><strong style={{ color: 'var(--text)' }}>{businessName}</strong><br /><span style={{ color: 'var(--text-dim)', fontSize: '14px' }}>{result?.customerNumber ? `Customer № ${result.customerNumber}` : 'Account created'}</span></span></li>
          <li><span className="obw-step-num" aria-hidden="true">✓</span><span><strong style={{ color: 'var(--text)' }}>{domainUrl}</strong><br /><span style={{ color: 'var(--text-dim)', fontSize: '14px' }}>Domain added — awaiting first beacon</span></span></li>
          <li><span className="obw-step-num" aria-hidden="true">{result?.emailSent === false ? '⚠️' : '✓'}</span><span><strong style={{ color: 'var(--text)' }}>{ownerEmail}</strong><br /><span style={{ color: 'var(--text-dim)', fontSize: '14px' }}>{result?.emailSent === false ? 'Account created, but the activation email did not send — resend from the dashboard.' : 'Activation email sent'}</span></span></li>
        </ul>
        <button
          type="button"
          className="obw-btn-primary"
          onClick={() =>
            onNext({
              provisionedAccountId: result?.accountId ?? '',
              provisionedCustomerNumber: result?.customerNumber ?? '',
              activationEmailSent: result?.emailSent !== false,
            })
          }
        >
          Done
        </button>
      </div>
    );
  }

  return (
    <div className="obw-step-content">
      <h2 className="obw-title">{c.heading}</h2>
      <p className="obw-subtitle">{c.body}</p>

      <ul className="obw-confirm-rows" aria-label="Review subscriber">
        <li>
          <span className="obw-confirm-row-label">Participant</span>
          <span className="obw-confirm-row-value">
            <strong style={{ color: 'var(--text)' }}>{businessName || '— business name missing —'}</strong>
            <br /><span style={{ color: 'var(--text-dim)', fontSize: '14px' }}>{ownerEmail || '— subscriber email missing —'}</span>
          </span>
        </li>
        <li>
          <span className="obw-confirm-row-label">Domain</span>
          <span className="obw-confirm-row-value">
            <strong style={{ color: 'var(--text)' }}>{domainUrl || '— domain missing —'}</strong>
          </span>
        </li>
        <li>
          <span className="obw-confirm-row-label">Plan</span>
          <span className="obw-confirm-row-value">
            <strong style={{ color: 'var(--text)' }}>{selectedTier ? (TIER_LABEL[selectedTier] ?? selectedTier) : '— plan missing —'}</strong>
            {promoCode && <><br /><span style={{ color: 'var(--text-dim)', fontSize: '14px' }}>Promo: {promoCode}</span></>}
          </span>
        </li>
        <li>
          <span className="obw-confirm-row-label">Consultant</span>
          <span className="obw-confirm-row-value">
            <strong style={{ color: 'var(--text)' }}>{nexaConsultant || '— none recorded —'}</strong>
          </span>
        </li>
      </ul>

      {error && (
        <div className="portal-wizard-error" role="alert">
          {error}
          {recover === 'email' && (
            <div style={{ marginTop: '10px' }}>
              <button type="button" className="obw-btn-secondary" onClick={() => onJumpToStep('profile')}>
                Change email
              </button>
            </div>
          )}
          {recover === 'domain' && (
            <div style={{ marginTop: '10px' }}>
              <button type="button" className="obw-btn-secondary" onClick={() => onJumpToStep('domain-verify')}>
                Change domain
              </button>
            </div>
          )}
          {recover === 'plan' && (
            <div style={{ marginTop: '10px' }}>
              <button type="button" className="obw-btn-secondary" onClick={() => onJumpToStep('subscribe')}>
                Choose a plan
              </button>
            </div>
          )}
        </div>
      )}

      <button
        type="button"
        className="obw-btn-primary"
        onClick={() => void provision()}
        disabled={phase === 'submitting'}
      >
        {phase === 'submitting' ? 'Creating…' : c.confirmCta}
      </button>
    </div>
  );
}
